/**
 * Logging Service
 * 
 * Centralized error and event logging with Firebase Analytics integration.
 * Provides consistent error tracking, performance monitoring, and debug logging.
 */

import { analytics } from '../FirebaseConfig';
import { logEvent } from 'firebase/analytics';

// Log levels
export const LogLevel = {
  DEBUG: 'debug',
  INFO: 'info',
  WARN: 'warn',
  ERROR: 'error'
};

// Environment check
const isDevelopment = process.env.NODE_ENV === 'development';

/**
 * Format error for logging
 */
const formatError = (error) => {
  if (error instanceof Error) {
    return {
      name: error.name,
      message: error.message,
      stack: error.stack,
      ...(error.code && { code: error.code }),
      ...(error.details && { details: error.details })
    };
  }
  return { message: String(error) };
};

/**
 * Get user context for logging
 */
const getUserContext = () => {
  // Try to get basic user info from localStorage
  const wixSession = localStorage.getItem('wix_session');
  if (wixSession) {
    try {
      const session = JSON.parse(wixSession);
      return {
        hasSession: true,
        sessionType: session.type || 'unknown'
      };
    } catch {
      return { hasSession: false };
    }
  }
  return { hasSession: false };
};

/**
 * Get device/browser context
 */
const getDeviceContext = () => ({
  userAgent: navigator.userAgent,
  platform: navigator.platform,
  language: navigator.language,
  online: navigator.onLine,
  viewportWidth: window.innerWidth,
  viewportHeight: window.innerHeight,
  url: window.location.href,
  referrer: document.referrer
});

/**
 * Console logging with styles
 */
const consoleLog = (level, message, data = {}) => {
  if (!isDevelopment) return;

  const styles = {
    debug: 'color: #6b7280',
    info: 'color: #3b82f6',
    warn: 'color: #f59e0b; font-weight: bold',
    error: 'color: #ef4444; font-weight: bold'
  };

  const prefix = `[${level.toUpperCase()}]`;
  
  if (Object.keys(data).length > 0) {
    console.groupCollapsed(`%c${prefix} ${message}`, styles[level]);
    console.log('Details:', data);
    console.groupEnd();
  } else {
    console.log(`%c${prefix} ${message}`, styles[level]);
  }
};

/**
 * Log to Firebase Analytics
 */
const logToAnalytics = (eventName, params = {}) => {
  try {
    if (analytics) {
      logEvent(analytics, eventName, {
        ...params,
        timestamp: new Date().toISOString()
      });
    }
  } catch (error) {
    // Silently fail analytics logging
    if (isDevelopment) {
      console.warn('Analytics logging failed:', error);
    }
  }
};

/**
 * Error storage for offline/batch reporting
 */
const errorQueue = [];
const MAX_QUEUE_SIZE = 50;

const queueError = (errorData) => {
  errorQueue.push({
    ...errorData,
    queuedAt: new Date().toISOString()
  });

  // Keep queue bounded
  if (errorQueue.length > MAX_QUEUE_SIZE) {
    errorQueue.shift();
  }
};

/**
 * Main Logger class
 */
class Logger {
  constructor(module = 'App') {
    this.module = module;
  }

  /**
   * Create a child logger with a specific module name
   */
  child(moduleName) {
    return new Logger(`${this.module}:${moduleName}`);
  }

  /**
   * Debug level logging (development only)
   */
  debug(message, data = {}) {
    consoleLog(LogLevel.DEBUG, `[${this.module}] ${message}`, data);
  }

  /**
   * Info level logging
   */
  info(message, data = {}) {
    consoleLog(LogLevel.INFO, `[${this.module}] ${message}`, data);
    
    logToAnalytics('app_info', {
      module: this.module,
      message: message.substring(0, 100),
      ...data
    });
  }

  /**
   * Warning level logging
   */
  warn(message, data = {}) {
    consoleLog(LogLevel.WARN, `[${this.module}] ${message}`, data);
    
    logToAnalytics('app_warning', {
      module: this.module,
      message: message.substring(0, 100),
      ...data
    });
  }

  /**
   * Error level logging
   */
  error(message, error = null, additionalData = {}) {
    const errorData = error ? formatError(error) : {};
    const logData = {
      module: this.module,
      message,
      error: errorData,
      user: getUserContext(),
      device: getDeviceContext(),
      ...additionalData
    };

    consoleLog(LogLevel.ERROR, `[${this.module}] ${message}`, logData);

    // Log to analytics
    logToAnalytics('app_error', {
      module: this.module,
      error_message: message.substring(0, 100),
      error_name: errorData.name || 'Unknown',
      error_code: errorData.code || 'none',
      url: window.location.pathname
    });

    // Queue for potential batch reporting
    queueError(logData);

    return logData;
  }

  /**
   * Track user actions
   */
  trackAction(action, data = {}) {
    consoleLog(LogLevel.INFO, `[${this.module}] Action: ${action}`, data);
    
    logToAnalytics('user_action', {
      module: this.module,
      action,
      ...data
    });
  }

  /**
   * Track page views
   */
  trackPageView(pageName, data = {}) {
    logToAnalytics('page_view', {
      page_name: pageName,
      page_location: window.location.href,
      page_path: window.location.pathname,
      ...data
    });
  }

  /**
   * Track feature usage
   */
  trackFeature(featureName, data = {}) {
    logToAnalytics('feature_used', {
      feature_name: featureName,
      module: this.module,
      ...data
    });
  }

  /**
   * Track performance metrics
   */
  trackPerformance(metricName, duration, data = {}) {
    consoleLog(LogLevel.DEBUG, `[${this.module}] Performance: ${metricName}`, { duration, ...data });
    
    logToAnalytics('performance_metric', {
      metric_name: metricName,
      duration_ms: Math.round(duration),
      module: this.module,
      ...data
    });
  }
}

// Create default logger instance
const logger = new Logger();

// Specialized loggers for common modules
export const authLogger = new Logger('Auth');
export const apiLogger = new Logger('API');
export const uiLogger = new Logger('UI');
export const wixLogger = new Logger('Wix');
export const firestoreLogger = new Logger('Firestore');

// Helper function to create timed operations
export const withTiming = async (operationName, operation, logger = apiLogger) => {
  const startTime = performance.now();
  try {
    const result = await operation();
    const duration = performance.now() - startTime;
    logger.trackPerformance(operationName, duration, { success: true });
    return result;
  } catch (error) {
    const duration = performance.now() - startTime;
    logger.trackPerformance(operationName, duration, { success: false });
    throw error;
  }
};

// Get queued errors (for batch reporting)
export const getQueuedErrors = () => [...errorQueue];
export const clearErrorQueue = () => errorQueue.length = 0;

export default logger;
